// src/lib/moderation.ts
// Signalement de contenu + blocage d'utilisateur (App Store Guideline 1.2).
// Tables : public.content_reports, public.blocked_users
// (voir supabase/sql/ugc_moderation.sql)

import { supabase } from "../supabaseClient";

export type ContentType = "play" | "club_post";

export const REPORT_REASONS = [
  "inappropriate",
  "harassment",
  "spam",
  "other",
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];

export async function reportContent(
  contentType: ContentType,
  contentId: string,
  contentOwnerId: string | null,
  reason: ReportReason,
  details?: string
): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("not_authenticated");

  const { error } = await supabase.from("content_reports").insert({
    reporter_id: user.id,
    content_type: contentType,
    content_id: contentId,
    content_owner_id: contentOwnerId,
    reason,
    details: details?.trim() ? details.trim() : null,
  });
  if (error) throw error;
}

export async function blockUser(userId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("not_authenticated");
  if (user.id === userId) throw new Error("cannot_block_self");

  const { error } = await supabase
    .from("blocked_users")
    .upsert({ blocker_id: user.id, blocked_id: userId }, { onConflict: "blocker_id,blocked_id" });
  if (error) throw error;
}

export async function unblockUser(userId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  await supabase
    .from("blocked_users")
    .delete()
    .eq("blocker_id", user.id)
    .eq("blocked_id", userId);
}

/** Ids des utilisateurs bloqués par l'utilisateur connecté (vide si non connecté). */
export async function getBlockedUserIds(): Promise<Set<string>> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return new Set();

  const { data, error } = await supabase
    .from("blocked_users")
    .select("blocked_id")
    .eq("blocker_id", user.id);

  if (error || !data) return new Set();
  return new Set(data.map((row) => row.blocked_id as string));
}
